import type { AreaId } from './world';

// ─────────────────────────────────────────────────────────────────────────────
// The ocean's words, in the language the class is learning: area names (shown
// when one is discovered) and the messages on signs and bottles (by index: the
// `i` of each sign in world.ts). Students tap them to translate, like everywhere else.
// Kept short and A1–A2. The Arabic texts should be checked by a native speaker.
// ─────────────────────────────────────────────────────────────────────────────

type Words = { areas: Record<AreaId, string>; signs: string[] };

const EN: Words = {
  areas: {
    shallows: 'Sunlit Shallows', meadow: 'Seagrass Meadow', reef: 'Coral Reef', kelp: 'Kelp Forest', wreck: 'Old Shipwreck',
    trench: 'The Deep Trench', caves: 'Crystal Caves', grotto: 'Crystal Grotto', echo: 'Echo Chamber', hollow: 'Deep Hollow', cabin: 'The Captain’s Cabin', temple: 'The Sunken Temple', abyss: 'The Abyss',
  },
  signs: [
    'Welcome to the ocean! Swim and explore.',
    'The coral is very colorful here.',
    'Swim through the arch, if you are brave.',
    'Danger! The cave is very dark.',
    'The crystals shine in the dark.',
    'Nobody has been here for a hundred years.',
    'The kelp is tall and green.',
    'This ship sank in a big storm.',
    'The trench is very deep. Are you afraid?',
    'You found the bottom of the ocean!',
    'The Sunken Temple. Only the brave go in!',
    'Something very big lives down here…',
  ],
};

const WORDS: Record<string, Words> = {
  en: EN,
  es: {
    areas: {
      shallows: 'Los Bajíos Soleados', meadow: 'La Pradera Marina', reef: 'El Arrecife de Coral', kelp: 'El Bosque de Algas', wreck: 'El Viejo Naufragio',
      trench: 'La Fosa Profunda', caves: 'Las Cuevas de Cristal', grotto: 'La Gruta de Cristal', echo: 'La Cámara del Eco', hollow: 'La Hondonada Profunda', cabin: 'El Camarote del Capitán', temple: 'El Templo Hundido', abyss: 'El Abismo',
    },
    signs: [
      '¡Bienvenidos al océano! Nada y explora.',
      'El coral es muy colorido aquí.',
      'Nada por el arco, si eres valiente.',
      '¡Peligro! La cueva está muy oscura.',
      'Los cristales brillan en la oscuridad.',
      'Nadie ha estado aquí en cien años.',
      'Las algas son altas y verdes.',
      'Este barco se hundió en una gran tormenta.',
      'La fosa es muy profunda. ¿Tienes miedo?',
      '¡Encontraste el fondo del océano!',
      'El Templo Hundido. ¡Solo entran los valientes!',
      'Algo muy grande vive aquí abajo…',
    ],
  },
  fr: {
    areas: {
      shallows: 'Les Hauts-Fonds Ensoleillés', meadow: 'La Prairie Marine', reef: 'Le Récif de Corail', kelp: 'La Forêt de Varech', wreck: 'La Vieille Épave',
      trench: 'La Fosse Profonde', caves: 'Les Grottes de Cristal', grotto: 'La Grotte de Cristal', echo: 'La Salle de l’Écho', hollow: 'Le Creux Profond', cabin: 'La Cabine du Capitaine', temple: 'Le Temple Englouti', abyss: 'L’Abîme',
    },
    signs: [
      'Bienvenue dans l’océan ! Nage et explore.',
      'Le corail est très coloré ici.',
      'Nage sous l’arche, si tu es courageux.',
      'Danger ! La grotte est très sombre.',
      'Les cristaux brillent dans le noir.',
      'Personne n’est venu ici depuis cent ans.',
      'Le varech est grand et vert.',
      'Ce bateau a coulé pendant une grosse tempête.',
      'La fosse est très profonde. Tu as peur ?',
      'Tu as trouvé le fond de l’océan !',
      'Le Temple Englouti. Seuls les courageux entrent !',
      'Quelque chose de très grand vit ici…',
    ],
  },
  de: {
    areas: {
      shallows: 'Die sonnigen Untiefen', meadow: 'Die Seegraswiese', reef: 'Das Korallenriff', kelp: 'Der Kelpwald', wreck: 'Das alte Schiffswrack',
      trench: 'Der tiefe Graben', caves: 'Die Kristallhöhlen', grotto: 'Die Kristallgrotte', echo: 'Die Echokammer', hollow: 'Die tiefe Senke', cabin: 'Die Kapitänskajüte', temple: 'Der versunkene Tempel', abyss: 'Der Abgrund',
    },
    signs: [
      'Willkommen im Ozean! Schwimm und entdecke.',
      'Die Korallen sind hier sehr bunt.',
      'Schwimm durch den Bogen, wenn du mutig bist.',
      'Gefahr! Die Höhle ist sehr dunkel.',
      'Die Kristalle leuchten im Dunkeln.',
      'Seit hundert Jahren war niemand hier.',
      'Der Kelp ist hoch und grün.',
      'Dieses Schiff ist in einem großen Sturm gesunken.',
      'Der Graben ist sehr tief. Hast du Angst?',
      'Du hast den Meeresboden gefunden!',
      'Der versunkene Tempel. Nur die Mutigen gehen hinein!',
      'Hier unten lebt etwas sehr Großes …',
    ],
  },
  it: {
    areas: {
      shallows: 'Le Secche Soleggiate', meadow: 'La Prateria Marina', reef: 'La Barriera Corallina', kelp: 'La Foresta di Alghe', wreck: 'Il Vecchio Relitto',
      trench: 'La Fossa Profonda', caves: 'Le Grotte di Cristallo', grotto: 'La Grotta di Cristallo', echo: 'La Camera dell’Eco', hollow: 'La Conca Profonda', cabin: 'La Cabina del Capitano', temple: 'Il Tempio Sommerso', abyss: 'L’Abisso',
    },
    signs: [
      'Benvenuto nell’oceano! Nuota ed esplora.',
      'Il corallo è molto colorato qui.',
      'Nuota sotto l’arco, se sei coraggioso.',
      'Pericolo! La grotta è molto buia.',
      'I cristalli brillano al buio.',
      'Nessuno è stato qui da cento anni.',
      'Le alghe sono alte e verdi.',
      'Questa nave è affondata in una grande tempesta.',
      'La fossa è molto profonda. Hai paura?',
      'Hai trovato il fondo dell’oceano!',
      'Il Tempio Sommerso. Entrano solo i coraggiosi!',
      'Qualcosa di molto grande vive quaggiù…',
    ],
  },
  pt: {
    areas: {
      shallows: 'Os Baixios Ensolarados', meadow: 'A Pradaria Marinha', reef: 'O Recife de Coral', kelp: 'A Floresta de Algas', wreck: 'O Velho Naufrágio',
      trench: 'A Fossa Profunda', caves: 'As Cavernas de Cristal', grotto: 'A Gruta de Cristal', echo: 'A Câmara do Eco', hollow: 'A Depressão Profunda', cabin: 'A Cabine do Capitão', temple: 'O Templo Submerso', abyss: 'O Abismo',
    },
    signs: [
      'Bem-vindo ao oceano! Nade e explore.',
      'O coral é muito colorido aqui.',
      'Nade pelo arco, se você for corajoso.',
      'Perigo! A caverna é muito escura.',
      'Os cristais brilham no escuro.',
      'Ninguém esteve aqui há cem anos.',
      'As algas são altas e verdes.',
      'Este navio afundou em uma grande tempestade.',
      'A fossa é muito profunda. Você tem medo?',
      'Você encontrou o fundo do oceano!',
      'O Templo Submerso. Só os corajosos entram!',
      'Algo muito grande vive aqui embaixo…',
    ],
  },
  ar: {
    areas: {
      shallows: 'المياه الضحلة المشمسة', meadow: 'مرج الأعشاب البحرية', reef: 'الشعاب المرجانية', kelp: 'غابة الأعشاب البحرية', wreck: 'حطام السفينة القديم',
      trench: 'الخندق العميق', caves: 'كهوف الكريستال', grotto: 'مغارة الكريستال', echo: 'غرفة الصدى', hollow: 'التجويف العميق', cabin: 'غرفة القبطان', temple: 'المعبد الغارق', abyss: 'الهاوية',
    },
    signs: [
      'مرحباً بك في المحيط! اسبح واستكشف.',
      'المرجان ملوّن جداً هنا.',
      'اسبح عبر القوس إذا كنت شجاعاً.',
      'خطر! الكهف مظلم جداً.',
      'الكريستالات تلمع في الظلام.',
      'لم يأتِ أحد إلى هنا منذ مئة عام.',
      'الأعشاب البحرية طويلة وخضراء.',
      'غرقت هذه السفينة في عاصفة كبيرة.',
      'الخندق عميق جداً. هل أنت خائف؟',
      'لقد وجدت قاع المحيط!',
      'المعبد الغارق. لا يدخله إلا الشجعان!',
      'شيء كبير جداً يعيش هنا في الأسفل…',
    ],
  },
  'ar-EG': {
    areas: {
      shallows: 'المية الضحلة المشمسة', meadow: 'مرج الحشايش البحرية', reef: 'الشعاب المرجانية', kelp: 'غابة الحشايش البحرية', wreck: 'المركب الغرقانة القديمة',
      trench: 'الخندق العميق', caves: 'كهوف الكريستال', grotto: 'مغارة الكريستال', echo: 'أوضة الصدى', hollow: 'الحفرة العميقة', cabin: 'أوضة القبطان', temple: 'المعبد الغرقان', abyss: 'الهاوية',
    },
    signs: [
      'أهلاً بيك في المحيط! عوم واستكشف.',
      'المرجان ألوانه حلوة أوي هنا.',
      'عوم من تحت القوس لو إنت شجاع.',
      'خطر! الكهف ضلمة أوي.',
      'الكريستال بيلمع في الضلمة.',
      'محدش جه هنا من مية سنة.',
      'الحشايش البحرية طويلة وخضرا.',
      'المركب دي غرقت في عاصفة كبيرة.',
      'الخندق عميق أوي. إنت خايف؟',
      'إنت لقيت قاع المحيط!',
      'المعبد الغرقان. محدش يدخل غير الشجعان!',
      'فيه حاجة كبيرة أوي عايشة هنا تحت…',
    ],
  },
};

export const oceanWords = (lang: string): Words => WORDS[lang] ?? WORDS[lang.split('-')[0]] ?? EN;
export const areaNameEn = (id: AreaId) => EN.areas[id];

// ── Fish names, in each class language (lib/aquarium.ts has the English) ────
// [es, fr, de, it, pt, ar, ar-EG]. The Arabic should be checked by a native speaker.
const FISH: Record<string, [string, string, string, string, string, string, string]> = {
  'sunny-perch': ['Perca Soleada', 'Perche Ensoleillée', 'Sonnenbarsch', 'Persico Solare', 'Perca Ensolarada', 'سمكة الفرخ المشمسة', 'سمكة الشمس'],
  'blue-tang': ['Pez Cirujano Azul', 'Chirurgien Bleu', 'Blauer Doktorfisch', 'Pesce Chirurgo Blu', 'Cirurgião Azul', 'سمكة الجراح الزرقاء', 'السمكة الجراح الزرقا'],
  clownfish: ['Pez Payaso', 'Poisson-clown', 'Clownfisch', 'Pesce Pagliaccio', 'Peixe-palhaço', 'سمكة المهرج', 'سمكة المهرج'],
  'silver-sardine': ['Sardina Plateada', 'Sardine Argentée', 'Silberne Sardine', 'Sardina Argentata', 'Sardinha Prateada', 'السردين الفضي', 'السردين الفضي'],
  'hermit-crab': ['Cangrejo Ermitaño', 'Bernard-l’ermite', 'Einsiedlerkrebs', 'Paguro', 'Caranguejo-eremita', 'السلطعون الناسك', 'الكابوريا الناسك'],
  'orange-starfish': ['Estrella de Mar Naranja', 'Étoile de Mer Orange', 'Oranger Seestern', 'Stella Marina Arancione', 'Estrela-do-mar Laranja', 'نجم البحر البرتقالي', 'نجمة البحر البرتقاني'],
  'moon-jelly': ['Medusa Luna', 'Méduse Lune', 'Ohrenqualle', 'Medusa Luna', 'Água-viva-da-lua', 'قنديل البحر القمري', 'قنديل البحر القمري'],
  'kelp-wrasse': ['Budión de las Algas', 'Labre des Algues', 'Tang-Lippfisch', 'Labro delle Alghe', 'Bodião das Algas', 'سمكة اللبروس', 'سمكة الطحالب'],
  'spotted-ray': ['Raya Moteada', 'Raie Tachetée', 'Gefleckter Rochen', 'Razza Maculata', 'Raia Pintada', 'الراي المرقط', 'الراي المنقط'],
  angelfish: ['Pez Ángel', 'Poisson-ange', 'Kaiserfisch', 'Pesce Angelo', 'Peixe-anjo', 'سمكة الملاك', 'سمكة الملاك'],
  butterflyfish: ['Pez Mariposa', 'Poisson-papillon', 'Falterfisch', 'Pesce Farfalla', 'Peixe-borboleta', 'سمكة الفراشة', 'سمكة الفراشة'],
  'spiky-puffer': ['Pez Globo', 'Poisson-globe', 'Kugelfisch', 'Pesce Palla', 'Baiacu', 'السمكة المنتفخة', 'السمكة النفاخة'],
  'brown-grouper': ['Mero Marrón', 'Mérou Brun', 'Brauner Zackenbarsch', 'Cernia Bruna', 'Garoupa Marrom', 'الهامور البني', 'الوقار البني'],
  'pink-squid': ['Calamar Rosa', 'Calmar Rose', 'Rosa Tintenfisch', 'Calamaro Rosa', 'Lula Rosa', 'الحبار الوردي', 'السبيط البمبي'],
  lanternfish: ['Pez Linterna', 'Poisson-lanterne', 'Laternenfisch', 'Pesce Lanterna', 'Peixe-lanterna', 'سمكة الفانوس', 'سمكة الفانوس'],
  seahorse: ['Caballito de Mar', 'Hippocampe', 'Seepferdchen', 'Cavalluccio Marino', 'Cavalo-marinho', 'فرس البحر', 'حصان البحر'],
  mandarinfish: ['Pez Mandarín', 'Poisson-mandarin', 'Mandarinfisch', 'Pesce Mandarino', 'Peixe-mandarim', 'سمكة الماندرين', 'سمكة الماندرين'],
  lionfish: ['Pez León', 'Poisson-lion', 'Rotfeuerfisch', 'Pesce Leone', 'Peixe-leão', 'سمكة الأسد', 'سمكة الأسد'],
  'crystal-jelly': ['Medusa de Cristal', 'Méduse de Cristal', 'Kristallqualle', 'Medusa di Cristallo', 'Água-viva de Cristal', 'قنديل البحر الكريستالي', 'قنديل البحر الكريستال'],
  'armored-crab': ['Cangrejo Acorazado', 'Crabe Cuirassé', 'Panzerkrabbe', 'Granchio Corazzato', 'Caranguejo Blindado', 'السلطعون المدرّع', 'الكابوريا المدرعة'],
  'glow-angler': ['Rape Brillante', 'Baudroie Lumineuse', 'Leuchtender Anglerfisch', 'Rana Pescatrice Luminosa', 'Peixe-pescador Brilhante', 'سمكة الصياد المضيئة', 'سمكة الصياد المنورة'],
  'golden-tang': ['Cirujano Dorado', 'Chirurgien Doré', 'Goldener Doktorfisch', 'Pesce Chirurgo Dorato', 'Cirurgião Dourado', 'الجراح الذهبي', 'الجراح الدهبي'],
  'ghost-ray': ['Raya Fantasma', 'Raie Fantôme', 'Geisterrochen', 'Razza Fantasma', 'Raia Fantasma', 'الراي الشبح', 'الراي العفريت'],
  'ruby-seahorse': ['Caballito de Rubí', 'Hippocampe Rubis', 'Rubin-Seepferdchen', 'Cavalluccio di Rubino', 'Cavalo-marinho Rubi', 'فرس البحر الياقوتي', 'حصان البحر الياقوتي'],
  'rainbow-fish': ['Pez Arcoíris', 'Poisson Arc-en-ciel', 'Regenbogenfisch', 'Pesce Arcobaleno', 'Peixe Arco-íris', 'سمكة قوس قزح', 'سمكة قوس قزح'],
  'pearl-dragon': ['Dragón de Perla', 'Dragon de Perle', 'Perlendrache', 'Drago di Perla', 'Dragão de Pérola', 'تنين اللؤلؤ', 'تنين اللولي'],
  'sea-otter': ['Nutria Marina', 'Loutre de Mer', 'Seeotter', 'Lontra Marina', 'Lontra-Marinha', 'ثعلب البحر', 'قضاعة البحر'],
  'baby-otter': ['Nutria Bebé', 'Bébé Loutre', 'Otterbaby', 'Lontrina', 'Lontra Bebê', 'قضاعة صغيرة', 'قضاعة صغيرة'],
  'golden-otter': ['Nutria Dorada', 'Loutre Dorée', 'Goldotter', "Lontra d'Oro", 'Lontra Dourada', 'القضاعة الذهبية', 'القضاعة الدهبي'],
  'starlight-eel': ['Anguila Estelar', 'Anguille Étoilée', 'Sternenaal', 'Anguilla Stellare', 'Enguia Estelar', 'أنقليس النجوم', 'تعبان النجوم'],
  'phoenix-fish': ['Pez Fénix', 'Poisson Phénix', 'Phönixfisch', 'Pesce Fenice', 'Peixe Fênix', 'سمكة العنقاء', 'سمكة العنقا'],
  'void-dragon': ['Dragón del Vacío', 'Dragon du Néant', 'Leerendrache', 'Drago del Vuoto', 'Dragão do Vazio', 'تنين الفراغ', 'تنين الفضا'],
};
const LANG_COL: Record<string, number> = { es: 0, fr: 1, de: 2, it: 3, pt: 4, ar: 5, 'ar-EG': 6 };
const colFor = (lang: string) => LANG_COL[lang] ?? LANG_COL[lang.split('-')[0]];

/** A fish's name in the class's language (English if there isn't one). */
export function fishName(id: string, english: string, lang: string): string {
  const c = colFor(lang);
  return c == null ? english : FISH[id]?.[c] ?? english;
}

// ── The turtle and the ghost captain (game/npcs.ts) ────────────────────────
export type NpcLines = {
  turtle: string; captain: string;
  turtleHi: string; captainHi: string;
  quests: Record<'turtle1' | 'turtle2' | 'turtle3' | 'captain1' | 'captain2' | 'captain3', string>;
  thanks: string; turtleDone: string; captainDone: string;
};
const NPC_EN: NpcLines = {
  turtle: 'Old Turtle', captain: 'The Ghost Captain',
  turtleHi: 'Hello, little fish! I am very old. Can you help me?',
  captainHi: 'Boo! I am the ghost captain. My ship sank a long time ago.',
  quests: {
    turtle1: 'I want to build a house. Bring me five blocks of sand, please.',
    turtle2: 'I love clownfish. Catch a clownfish and bring it to me.',
    turtle3: 'Pearls are very pretty. Bring me three pearls.',
    captain1: 'My ship is broken. Bring me six pieces of wood.',
    captain2: 'I miss my pet pufferfish. Bring me a pufferfish!',
    captain3: 'I love treasure! Bring me two crystals.',
  },
  thanks: 'Thank you! Here is a present for you.',
  turtleDone: 'Thank you, friend. You are very kind!',
  captainDone: 'Thank you, sailor! Now the sea is yours.',
};
const NPC: Record<string, NpcLines> = {
  en: NPC_EN,
  es: {
    turtle: 'La Tortuga Vieja', captain: 'El Capitán Fantasma',
    turtleHi: '¡Hola, pececito! Soy muy vieja. ¿Me puedes ayudar?',
    captainHi: '¡Bu! Soy el capitán fantasma. Mi barco se hundió hace mucho tiempo.',
    quests: {
      turtle1: 'Quiero construir una casa. Tráeme cinco bloques de arena, por favor.',
      turtle2: 'Me encantan los peces payaso. Atrapa un pez payaso y tráemelo.',
      turtle3: 'Las perlas son muy bonitas. Tráeme tres perlas.',
      captain1: 'Mi barco está roto. Tráeme seis piezas de madera.',
      captain2: 'Extraño a mi pez globo. ¡Tráeme un pez globo!',
      captain3: '¡Me encantan los tesoros! Tráeme dos cristales.',
    },
    thanks: '¡Gracias! Aquí tienes un regalo.',
    turtleDone: 'Gracias, amigo. ¡Eres muy amable!',
    captainDone: '¡Gracias, marinero! Ahora el mar es tuyo.',
  },
  fr: {
    turtle: 'La Vieille Tortue', captain: 'Le Capitaine Fantôme',
    turtleHi: 'Bonjour, petit poisson ! Je suis très vieille. Tu peux m’aider ?',
    captainHi: 'Bouh ! Je suis le capitaine fantôme. Mon bateau a coulé il y a longtemps.',
    quests: {
      turtle1: 'Je veux construire une maison. Apporte-moi cinq blocs de sable, s’il te plaît.',
      turtle2: 'J’adore les poissons-clowns. Attrape un poisson-clown et apporte-le-moi.',
      turtle3: 'Les perles sont très jolies. Apporte-moi trois perles.',
      captain1: 'Mon bateau est cassé. Apporte-moi six morceaux de bois.',
      captain2: 'Mon poisson-globe me manque. Apporte-moi un poisson-globe !',
      captain3: 'J’adore les trésors ! Apporte-moi deux cristaux.',
    },
    thanks: 'Merci ! Voici un cadeau pour toi.',
    turtleDone: 'Merci, mon ami. Tu es très gentil !',
    captainDone: 'Merci, matelot ! Maintenant, la mer est à toi.',
  },
  de: {
    turtle: 'Die alte Schildkröte', captain: 'Der Geisterkapitän',
    turtleHi: 'Hallo, kleiner Fisch! Ich bin sehr alt. Kannst du mir helfen?',
    captainHi: 'Buh! Ich bin der Geisterkapitän. Mein Schiff ist vor langer Zeit gesunken.',
    quests: {
      turtle1: 'Ich will ein Haus bauen. Bring mir bitte fünf Blöcke Sand.',
      turtle2: 'Ich liebe Clownfische. Fang einen Clownfisch und bring ihn mir.',
      turtle3: 'Perlen sind sehr schön. Bring mir drei Perlen.',
      captain1: 'Mein Schiff ist kaputt. Bring mir sechs Stücke Holz.',
      captain2: 'Ich vermisse meinen Kugelfisch. Bring mir einen Kugelfisch!',
      captain3: 'Ich liebe Schätze! Bring mir zwei Kristalle.',
    },
    thanks: 'Danke! Hier ist ein Geschenk für dich.',
    turtleDone: 'Danke, mein Freund. Du bist sehr nett!',
    captainDone: 'Danke, Matrose! Jetzt gehört das Meer dir.',
  },
  it: {
    turtle: 'La Vecchia Tartaruga', captain: 'Il Capitano Fantasma',
    turtleHi: 'Ciao, pesciolino! Sono molto vecchia. Mi puoi aiutare?',
    captainHi: 'Bu! Sono il capitano fantasma. La mia nave è affondata tanto tempo fa.',
    quests: {
      turtle1: 'Voglio costruire una casa. Portami cinque blocchi di sabbia, per favore.',
      turtle2: 'Amo i pesci pagliaccio. Cattura un pesce pagliaccio e portamelo.',
      turtle3: 'Le perle sono molto belle. Portami tre perle.',
      captain1: 'La mia nave è rotta. Portami sei pezzi di legno.',
      captain2: 'Mi manca il mio pesce palla. Portami un pesce palla!',
      captain3: 'Amo i tesori! Portami due cristalli.',
    },
    thanks: 'Grazie! Ecco un regalo per te.',
    turtleDone: 'Grazie, amico. Sei molto gentile!',
    captainDone: 'Grazie, marinaio! Ora il mare è tuo.',
  },
  pt: {
    turtle: 'A Tartaruga Velha', captain: 'O Capitão Fantasma',
    turtleHi: 'Olá, peixinho! Eu sou muito velha. Você pode me ajudar?',
    captainHi: 'Bu! Eu sou o capitão fantasma. Meu navio afundou há muito tempo.',
    quests: {
      turtle1: 'Quero construir uma casa. Traga-me cinco blocos de areia, por favor.',
      turtle2: 'Eu amo peixes-palhaço. Pegue um peixe-palhaço e traga para mim.',
      turtle3: 'As pérolas são muito bonitas. Traga-me três pérolas.',
      captain1: 'Meu navio está quebrado. Traga-me seis pedaços de madeira.',
      captain2: 'Sinto falta do meu baiacu. Traga-me um baiacu!',
      captain3: 'Eu amo tesouros! Traga-me dois cristais.',
    },
    thanks: 'Obrigado! Aqui está um presente para você.',
    turtleDone: 'Obrigada, amigo. Você é muito gentil!',
    captainDone: 'Obrigado, marinheiro! Agora o mar é seu.',
  },
  ar: {
    turtle: 'السلحفاة العجوز', captain: 'القبطان الشبح',
    turtleHi: 'مرحباً أيتها السمكة الصغيرة! أنا كبيرة جداً في السن. هل يمكنك مساعدتي؟',
    captainHi: 'بوو! أنا القبطان الشبح. غرقت سفينتي منذ زمن طويل.',
    quests: {
      turtle1: 'أريد أن أبني بيتاً. أحضر لي خمس كتل من الرمل من فضلك.',
      turtle2: 'أحب سمك المهرج. اصطد سمكة مهرج وأحضرها لي.',
      turtle3: 'اللآلئ جميلة جداً. أحضر لي ثلاث لآلئ.',
      captain1: 'سفينتي مكسورة. أحضر لي ست قطع من الخشب.',
      captain2: 'أشتاق إلى سمكتي المنتفخة. أحضر لي سمكة منتفخة!',
      captain3: 'أحب الكنوز! أحضر لي قطعتين من الكريستال.',
    },
    thanks: 'شكراً! هذه هدية لك.',
    turtleDone: 'شكراً يا صديقي. أنت لطيف جداً!',
    captainDone: 'شكراً أيها البحّار! الآن البحر لك.',
  },
  'ar-EG': {
    turtle: 'السلحفة العجوزة', captain: 'القبطان العفريت',
    turtleHi: 'أهلاً يا سمكة صغيرة! أنا كبيرة أوي. ممكن تساعدني؟',
    captainHi: 'بخ! أنا القبطان العفريت. مركبي غرقت من زمان أوي.',
    quests: {
      turtle1: 'عايزة أبني بيت. هاتلي خمس حتت رملة لو سمحت.',
      turtle2: 'بحب سمك المهرج. اصطاد سمكة مهرج وهاتهالي.',
      turtle3: 'اللولي حلو أوي. هاتلي تلات لولياية.',
      captain1: 'مركبي مكسورة. هاتلي ست حتت خشب.',
      captain2: 'وحشتني السمكة النفاخة بتاعتي. هاتلي سمكة نفاخة!',
      captain3: 'بحب الكنوز! هاتلي حتتين كريستال.',
    },
    thanks: 'شكراً! دي هدية ليك.',
    turtleDone: 'شكراً يا صاحبي. إنت ذوق أوي!',
    captainDone: 'شكراً يا بحّار! دلوقتي البحر بتاعك.',
  },
};
export const npcLines = (lang: string): NpcLines => NPC[lang] ?? NPC[lang.split('-')[0]] ?? NPC_EN;
export const englishSigns = () => EN.signs;
